import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

import { API_BASE_URL } from '../../config';
import { IAsset } from '../../types/damTypes';
import {
  createImpactCollection,
  fetchProductReferenceImpactPreflight,
  fetchRecipeReferenceImpactPreflight,
} from './api';
import type {
  ProductReferenceImpactItem,
  ProductReferenceImpactPreflightResponse,
  RecipeReferenceImpactItem,
  RecipeReferenceImpactPreflightResponse,
} from './types';
import {
  getProductImpactCollectionName,
  getRecipeImpactCollectionName,
} from './utils';
import {
  getWordPressArchiveUnpublishTargetCount,
  WordPressArchiveUnpublishImpact,
} from '../wordpressPublishing/archiveUnpublishGuard';

interface RelationImpactDialogProps {
  open: boolean;
  action: 'archive' | 'delete';
  asset?: IAsset | null;
  assets?: IAsset[];
  assetIds?: string[];
  wordPressImpacts?: WordPressArchiveUnpublishImpact[];
  onClose: () => void;
  onConfirm: () => Promise<void>;
  onConfirmWithWordPressUnpublish?: () => Promise<void>;
  onCollectionUpdated?: () => void;
}

interface CsvRelationRow {
  sourceType: 'Produkt' | 'Recept';
  sourceTitle: string;
  sourceFileName: string;
  sourceArticleNumber: string;
  relatedType: 'Produkt' | 'Recept';
  relatedTitle: string;
  relatedFileName: string;
  relatedArticleNumber: string;
  relationType: string;
  status: string;
}

function getErrorMessage(error: any, fallback: string): string {
  return String(error?.response?.data?.message || error?.message || fallback);
}

function uniqueStrings(values: Array<string | undefined | null>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  values.forEach((value) => {
    const normalized = String(value || '').trim();
    if (!normalized || seen.has(normalized)) return;
    seen.add(normalized);
    result.push(normalized);
  });

  return result;
}

function escapeCsvCell(value: string): string {
  return `"${String(value || '').replace(/"/g, '""')}"`;
}

function buildCsv(rows: CsvRelationRow[]): string {
  const header = [
    'Kalla typ',
    'Kalla',
    'Kalla filnamn',
    'Kalla artikelnummer',
    'Relaterad typ',
    'Relaterad asset',
    'Relaterad filnamn',
    'Relaterad artikelnummer',
    'Relationstyp',
    'Status',
  ];

  const lines = rows.map((row) => [
    row.sourceType,
    row.sourceTitle,
    row.sourceFileName,
    row.sourceArticleNumber,
    row.relatedType,
    row.relatedTitle,
    row.relatedFileName,
    row.relatedArticleNumber,
    row.relationType,
    row.status,
  ].map(escapeCsvCell).join(','));

  return [header.map(escapeCsvCell).join(','), ...lines].join('\r\n');
}

function getStatusLabel(item: { archived?: boolean; trashed?: boolean }): string {
  const labels: string[] = [];
  if (item.archived) labels.push('Arkiverad');
  if (item.trashed) labels.push('I papperskorg');
  return labels.join(', ');
}

function RelationPreview({
  previewUrl,
  fileName,
}: {
  previewUrl?: string;
  fileName: string;
}) {
  return (
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 1,
        backgroundColor: '#f7fafc',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {previewUrl ? (
        <Box
          component="img"
          src={`${API_BASE_URL}${previewUrl}`}
          alt={fileName}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Ingen bild
        </Typography>
      )}
    </Box>
  );
}

const RelationImpactDialog: React.FC<RelationImpactDialogProps> = ({
  open,
  action,
  asset,
  assets,
  assetIds,
  wordPressImpacts = [],
  onClose,
  onConfirm,
  onConfirmWithWordPressUnpublish,
  onCollectionUpdated,
}) => {
  const [recipePreflight, setRecipePreflight] = useState<RecipeReferenceImpactPreflightResponse | null>(null);
  const [productPreflight, setProductPreflight] = useState<ProductReferenceImpactPreflightResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [showSkipWordPressWarning, setShowSkipWordPressWarning] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [collectionMessage, setCollectionMessage] = useState<string | null>(null);

  const sourceAssetIds = useMemo(() => uniqueStrings([
    ...(assetIds || []),
    ...((assets || []).map((item) => item._id)),
    asset?._id,
  ]), [asset?._id, assetIds, assets]);

  useEffect(() => {
    if (!open || sourceAssetIds.length === 0) {
      if (!open) {
        setRecipePreflight(null);
        setProductPreflight(null);
        setLoading(false);
        setConfirming(false);
        setCreatingCollection(false);
        setShowSkipWordPressWarning(false);
        setErrorMessage(null);
        setCollectionMessage(null);
      }
      return;
    }

    let isCancelled = false;

    const loadPreflight = async () => {
      setLoading(true);
      setErrorMessage(null);
      setCollectionMessage(null);

      try {
        const [recipeResult, productResult] = await Promise.all([
          fetchRecipeReferenceImpactPreflight(sourceAssetIds),
          fetchProductReferenceImpactPreflight(sourceAssetIds),
        ]);
        if (isCancelled) return;
        setRecipePreflight(recipeResult);
        setProductPreflight(productResult);
      } catch (error: any) {
        if (isCancelled) return;
        setErrorMessage(getErrorMessage(error, 'Kunde inte läsa påverkade relationer.'));
        setRecipePreflight(null);
        setProductPreflight(null);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    void loadPreflight();

    return () => {
      isCancelled = true;
    };
  }, [open, sourceAssetIds]);

  const recipeItems = useMemo(() => (
    (recipePreflight?.items || []).filter((item) => item.impactedCount > 0)
  ), [recipePreflight]);

  const productItems = useMemo(() => (
    (productPreflight?.items || []).filter((item) => item.impactedCount > 0)
  ), [productPreflight]);

  const csvRows = useMemo<CsvRelationRow[]>(() => {
    const rows: CsvRelationRow[] = [];

    recipeItems.forEach((item) => {
      item.impactedProducts.forEach((product) => {
        rows.push({
          sourceType: 'Recept',
          sourceTitle: item.sourceTitle,
          sourceFileName: item.sourceFileName,
          sourceArticleNumber: '',
          relatedType: 'Produkt',
          relatedTitle: product.title,
          relatedFileName: product.fileName,
          relatedArticleNumber: product.articleNumber || '',
          relationType: 'Produkter i recept (art nr)',
          status: getStatusLabel(product),
        });
      });
    });

    productItems.forEach((item) => {
      item.impactedRecipes.forEach((recipe) => {
        rows.push({
          sourceType: 'Produkt',
          sourceTitle: item.sourceTitle,
          sourceFileName: item.sourceFileName,
          sourceArticleNumber: item.sourceArticleNumber || '',
          relatedType: 'Recept',
          relatedTitle: recipe.title,
          relatedFileName: recipe.fileName,
          relatedArticleNumber: '',
          relationType: 'Relaterade Recept - Produkter',
          status: getStatusLabel(recipe),
        });
      });
    });

    return rows;
  }, [productItems, recipeItems]);

  const impactedProductIds = useMemo(() => uniqueStrings(
    recipeItems.flatMap((item) => item.impactedProducts.map((product) => product.assetId)),
  ), [recipeItems]);
  const impactedRecipeIds = useMemo(() => uniqueStrings(
    productItems.flatMap((item) => item.impactedRecipes.map((recipe) => recipe.assetId)),
  ), [productItems]);

  const actionLabel = action === 'delete' ? 'raderas' : 'arkiveras';
  const confirmLabel = action === 'delete' ? 'Fortsätt radera' : 'Fortsätt arkivera';
  const wordPressTargetCount = getWordPressArchiveUnpublishTargetCount(wordPressImpacts);
  const hasWordPressImpacts = wordPressTargetCount > 0;
  const hasProductSources = productItems.length > 0 || (productPreflight?.items || []).length > 0;
  const hasRelationImpacts = csvRows.length > 0 || hasProductSources;
  const title = hasWordPressImpacts && !hasRelationImpacts
    ? (action === 'delete' ? 'Radera publicerad asset' : 'Arkivera publicerad asset')
    : (action === 'delete' ? 'Radera med relationer' : 'Arkivera med relationer');
  const unpublishConfirmLabel = action === 'delete' ? 'Avpublicera och radera' : 'Avpublicera och arkivera';

  const handleExportCsv = () => {
    if (csvRows.length === 0) return;

    const blob = new Blob([buildCsv(csvRows)], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relation-impact-${action}-${Date.now()}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleCreateCollection = async () => {
    if (impactedProductIds.length === 0) return;

    setCreatingCollection(true);
    setErrorMessage(null);

    try {
      const { collectionName } = await createImpactCollection(
        getRecipeImpactCollectionName(asset || null, recipeItems[0]?.sourceTitle),
        impactedProductIds,
      );
      setCollectionMessage(`Samlingen "${collectionName}" skapades med ${impactedProductIds.length} produkter.`);
      onCollectionUpdated?.();
    } catch (error: any) {
      setErrorMessage(getErrorMessage(error, 'Kunde inte skapa samling av påverkade produkter.'));
    } finally {
      setCreatingCollection(false);
    }
  };

  const handleCreateRecipeCollection = async () => {
    if (impactedRecipeIds.length === 0) return;

    setCreatingCollection(true);
    setErrorMessage(null);

    try {
      const { collectionName } = await createImpactCollection(
        getProductImpactCollectionName(asset || null, productItems[0]?.sourceTitle),
        impactedRecipeIds,
      );
      setCollectionMessage(`Samlingen "${collectionName}" skapades med ${impactedRecipeIds.length} recept.`);
      onCollectionUpdated?.();
    } catch (error: any) {
      setErrorMessage(getErrorMessage(error, 'Kunde inte skapa samling av påverkade recept.'));
    } finally {
      setCreatingCollection(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    setErrorMessage(null);

    try {
      await onConfirm();
      onClose();
    } catch (error: any) {
      setErrorMessage(getErrorMessage(error, `Kunde inte slutföra åtgärden när asset ${actionLabel}.`));
    } finally {
      setConfirming(false);
    }
  };

  const handleConfirmWithWordPressUnpublish = async () => {
    if (!onConfirmWithWordPressUnpublish) {
      await handleConfirm();
      return;
    }

    setConfirming(true);
    setErrorMessage(null);

    try {
      await onConfirmWithWordPressUnpublish();
      onClose();
    } catch (error: any) {
      setErrorMessage(getErrorMessage(error, `Kunde inte avpublicera och slutföra åtgärden när asset ${actionLabel}.`));
    } finally {
      setConfirming(false);
    }
  };

  const handleConfirmWithoutWordPressUnpublish = async () => {
    if (hasWordPressImpacts && !showSkipWordPressWarning) {
      setShowSkipWordPressWarning(true);
      return;
    }

    await handleConfirm();
  };

  const renderRecipeSection = (item: RecipeReferenceImpactItem) => (
    <Box key={`recipe-${item.sourceAssetId}`} sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Recept: {item.sourceTitle}
      </Typography>
      <Box sx={{ display: 'grid', gap: 1 }}>
        {item.impactedProducts.map((product) => (
          <Box key={product.assetId} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
            <RelationPreview previewUrl={product.previewUrl} fileName={product.fileName} />
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                {product.title || product.fileName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>
                {product.articleNumber ? `Art nr: ${product.articleNumber} · ` : ''}
                {product.fileName}
              </Typography>
            </Box>
            {product.archived && <Chip label="Arkiverad" size="small" variant="outlined" />}
            {product.trashed && <Chip label="I papperskorg" size="small" variant="outlined" />}
          </Box>
        ))}
      </Box>
    </Box>
  );

  const renderProductSection = (item: ProductReferenceImpactItem) => (
    <Box key={`product-${item.sourceAssetId}`} sx={{ mb: 2.5 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Produkt: {item.sourceTitle}
        {item.sourceArticleNumber ? ` (${item.sourceArticleNumber})` : ''}
      </Typography>
      <Box sx={{ display: 'grid', gap: 1 }}>
        {item.impactedRecipes.map((recipe) => (
          <Box key={recipe.assetId} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.25, border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
            <RelationPreview previewUrl={recipe.previewUrl} fileName={recipe.fileName} />
            <Box sx={{ minWidth: 0, flexGrow: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                {recipe.title || recipe.fileName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-word' }}>
                {recipe.fileName}
              </Typography>
            </Box>
            {recipe.archived && <Chip label="Arkiverad" size="small" variant="outlined" />}
            {recipe.trashed && <Chip label="I papperskorg" size="small" variant="outlined" />}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <Dialog open={open} onClose={confirming || creatingCollection ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        {hasRelationImpacts ? (
          <>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Valda produkter eller recept {actionLabel}. Relationerna nedan tas bort från motsatt sida efter bekräftelse.
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
              Om asseten senare återställs kommer relationerna inte tillbaka automatiskt.
            </Typography>
          </>
        ) : (
          <Typography variant="body1" sx={{ mb: 2 }}>
            Den här asseten {actionLabel}. Kontrollera WordPress-avpublicering innan du fortsätter.
          </Typography>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {!loading && collectionMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {collectionMessage}
          </Alert>
        )}

        {!loading && hasWordPressImpacts && (
          <Alert severity={showSkipWordPressWarning ? 'error' : 'warning'} sx={{ mb: 2 }}>
            {showSkipWordPressWarning
              ? `Om du fortsätter utan att avpublicera kan innehållet ligga kvar publikt i WordPress trots att asseten ${actionLabel} i Athena. Athena är master, så detta bör bara göras om du är helt säker.`
              : `${wordPressImpacts.length} asset${wordPressImpacts.length === 1 ? '' : 's'} har ${wordPressTargetCount} aktiv${wordPressTargetCount === 1 ? '' : 'a'} WordPress-publicering${wordPressTargetCount === 1 ? '' : 'ar'}. Avpublicera innan asseten ${actionLabel}.`}
          </Alert>
        )}

        {!loading && hasProductSources && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Kom ihåg att gå igenom de recept som är relaterade till denna produkt. Produktnamnet kan finnas i Ingredienser eller i Instruktioner.
          </Alert>
        )}

        {!loading && (
          <>
            <Alert severity={csvRows.length > 0 ? 'warning' : 'info'} sx={{ mb: 2 }}>
              {csvRows.length > 0
                ? `${csvRows.length} relationer påverkas av den här åtgärden.`
                : 'Inga relationer hittades för valda produkter eller recept.'}
            </Alert>

            {csvRows.length > 0 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 2, flexWrap: 'wrap' }}>
                  <Typography variant="subtitle2">
                    Påverkade relationer
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {impactedProductIds.length > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={creatingCollection || confirming}
                        onClick={() => {
                          void handleCreateCollection();
                        }}
                      >
                        {creatingCollection ? 'Skapar samling...' : 'Skapa samling av produkter'}
                      </Button>
                    )}
                    {impactedRecipeIds.length > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={creatingCollection || confirming}
                        onClick={() => {
                          void handleCreateRecipeCollection();
                        }}
                      >
                        {creatingCollection ? 'Skapar samling...' : 'Skapa samling av dessa recept'}
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={confirming || creatingCollection}
                      onClick={handleExportCsv}
                    >
                      Exportera CSV
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ maxHeight: 420, overflowY: 'auto', pr: 0.5 }}>
                  {recipeItems.map(renderRecipeSection)}
                  {productItems.map(renderProductSection)}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={confirming || creatingCollection}>
          Avbryt
        </Button>
        {hasWordPressImpacts && (
          <Button
            variant="outlined"
            color="warning"
            onClick={() => {
              void handleConfirmWithoutWordPressUnpublish();
            }}
            disabled={loading || confirming || creatingCollection}
          >
            {showSkipWordPressWarning ? confirmLabel : 'Fortsätt utan avpublicering'}
          </Button>
        )}
        <Button
          variant="contained"
          color={action === 'delete' ? 'error' : 'primary'}
          onClick={() => {
            void (hasWordPressImpacts ? handleConfirmWithWordPressUnpublish() : handleConfirm());
          }}
          disabled={loading || confirming || creatingCollection}
        >
          {confirming ? 'Arbetar...' : (hasWordPressImpacts ? unpublishConfirmLabel : confirmLabel)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RelationImpactDialog;
